import { colors } from '@ardoq/design-tokens';
import { logWarn } from '@ardoq/logging';
import { CANVAS_MAXIMUM_DIMENSION } from './consts';
import { createExportCanvas } from './exportUtil';
import { ExportTrackingData, GetStyleSheetsFn } from './types';
import * as profiling from '@ardoq/profiling';
import { getCanvasOfDOM } from './getCanvasOfDOM';

const WINDOW_MARGIN = 10;

type CanvasSize = { width: number; height: number };

/**
 * Whether the legend can be composited beside the diagram without taking the
 * result past the canvas dimension limit. The diagram canvas may already sit at
 * that limit — the direct export paths size it there deliberately — and widening
 * it for the legend would produce a canvas the browser hands back blank.
 *
 * Only the dimension limit is tested. `directExportScale` saturates the canvas
 * area limit exactly when the area term binds, so the diagram canvas is already
 * marginally over it before any legend is measured; testing it here would drop
 * the legend from every area-clamped export, including the ones that render fine
 * today. That limit is about memory rather than a hard browser refusal, and it is
 * the scale clamp's to enforce.
 *
 * `legendWithGap` is the space the legend occupies in the composite — its canvas
 * plus the gap it is drawn after — since that is what the output canvas is sized
 * to.
 */
export const legendFitsBeside = (
  diagram: CanvasSize,
  legendWithGap: CanvasSize
): boolean =>
  diagram.width + legendWithGap.width <= CANVAS_MAXIMUM_DIMENSION &&
  Math.max(diagram.height, legendWithGap.height) <= CANVAS_MAXIMUM_DIMENSION;

const headerFitsAbove = (
  body: CanvasSize,
  headerWithGap: CanvasSize
): boolean =>
  Math.max(body.width, headerWithGap.width) <= CANVAS_MAXIMUM_DIMENSION &&
  body.height + headerWithGap.height <= CANVAS_MAXIMUM_DIMENSION;

type CanvasWithBackgroundColorArguments = {
  profilingTransaction: profiling.Transaction;
  mutableTrackingData: ExportTrackingData;
  inputCanvas: HTMLCanvasElement | OffscreenCanvas;
  color: string;
  getStyleSheets: GetStyleSheetsFn;
  viewLegend?: HTMLElement | null;
  viewHeader?: HTMLElement | null;
};

export const getCanvasWithBackgroundColor = async ({
  profilingTransaction,
  mutableTrackingData,
  inputCanvas,
  color,
  getStyleSheets,
  viewLegend,
  viewHeader,
}: CanvasWithBackgroundColorArguments): Promise<HTMLCanvasElement> => {
  let legendCanvas: HTMLCanvasElement | null = null;
  if (viewLegend) {
    legendCanvas = await getCanvasOfDOM(
      viewLegend,
      'viewLegend',
      getStyleSheets,
      mutableTrackingData,
      profilingTransaction
    );
  }

  const legendWidth = legendCanvas ? legendCanvas.width + WINDOW_MARGIN : 0;
  const legendHeight = legendCanvas ? legendCanvas.height + WINDOW_MARGIN : 0;

  // legendWidth/Height already carry WINDOW_MARGIN, which is what the composite
  // below is sized to.
  const legendFits = legendFitsBeside(
    { width: inputCanvas.width, height: inputCanvas.height },
    { width: legendWidth, height: legendHeight }
  );
  if (legendCanvas && !legendFits) {
    logWarn(
      new Error(
        'Export: the legend does not fit beside the diagram within the canvas limit; exporting without it'
      )
    );
  }
  const legendToDraw = legendFits ? legendCanvas : null;

  const bodyWidth = inputCanvas.width + (legendToDraw ? legendWidth : 0);
  const bodyHeight =
    legendToDraw && legendHeight > inputCanvas.height
      ? legendHeight
      : inputCanvas.height;

  const headerCanvas = viewHeader
    ? await getCanvasOfDOM(
        viewHeader,
        'viewHeader',
        getStyleSheets,
        mutableTrackingData,
        profilingTransaction
      )
    : null;
  const headerWithGap = headerCanvas
    ? {
        width: headerCanvas.width + 2 * WINDOW_MARGIN,
        height: headerCanvas.height + 2 * WINDOW_MARGIN,
      }
    : { width: 0, height: 0 };
  const headerFits = headerFitsAbove(
    { width: bodyWidth, height: bodyHeight },
    headerWithGap
  );
  if (headerCanvas && !headerFits) {
    logWarn(
      new Error(
        'Export: the header does not fit above the diagram within the canvas limit; exporting without it'
      )
    );
  }
  const headerToDraw = headerFits ? headerCanvas : null;
  const headerOffset = headerToDraw ? headerWithGap.height : 0;

  const outputCanvas = createExportCanvas();
  const outputCanvasCtx = outputCanvas.getContext('2d');
  outputCanvas.width = headerToDraw
    ? Math.max(bodyWidth, headerWithGap.width)
    : bodyWidth;
  outputCanvas.height = headerOffset + bodyHeight;

  if (outputCanvasCtx) {
    outputCanvasCtx.fillStyle = color || colors.white950;
    outputCanvasCtx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    outputCanvasCtx.drawImage(inputCanvas, 0, headerOffset);

    if (legendToDraw) {
      outputCanvasCtx.drawImage(
        legendToDraw,
        inputCanvas.width,
        headerOffset,
        legendToDraw.width,
        legendToDraw.height
      );
    }

    if (headerToDraw) {
      outputCanvasCtx.drawImage(
        headerToDraw,
        outputCanvas.width - WINDOW_MARGIN - headerToDraw.width,
        WINDOW_MARGIN
      );
    }
  }

  return outputCanvas;
};
